import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PageBreadcrumbs, { THEMES } from "@ucs/Overrides/Components/PageBreadcrumbs";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    breadcrumbsEnabled = "breadcrumbsEnabled",
    image = "image",
    focalPoint = "focalPoint",
    title = "title",
    description = "description",
    imageBannerHeight = "imageBannerHeight",
    imageColorOverlay = "imageColorOverlay",
    imageColorOverlayGradient = "imageColorOverlayGradient",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.breadcrumbsEnabled]: boolean;
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.title]: string;
        [fields.description]: string;
        [fields.imageBannerHeight]: number;
        [fields.imageColorOverlay]: string;
        [fields.imageColorOverlayGradient]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

export const NewUCSBanner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const backgroundStyles = `
        background-image: url(${parseImageUrl(fields.image).url});
        background-size: cover;
    `;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            height: ${fields.imageBannerHeight}px;
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.imageColorOverlay};
            ${fields.imageColorOverlayGradient ? `background: linear-gradient(${fields.imageColorOverlayGradient});` : ""}
            padding: 50px;

        `,
    };

    return (
        <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
            <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                {fields.breadcrumbsEnabled && <PageBreadcrumbs theme={THEMES.LIGHT}></PageBreadcrumbs>}
                <StyledWrapper className="banner-center-wrapper" {...styles.slideCenteringWrapperStyles}>
                    <Typography className="banner-heading">{parse(fields.title, parserOptions)}</Typography>
                    <Typography className="banner-subheading">{parse(fields.description, parserOptions)}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const newUCSBanner: WidgetModule = {
    component: NewUCSBanner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.title,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description/Subtitle",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
            },
            {
                fieldType: "General",
                name: fields.imageBannerHeight,
                displayName: "Image Banner Height",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 400,
            },
            {
                fieldType: "General",
                name: fields.imageColorOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "rgba(0, 0, 0, 0.35)",
            },
            {
                fieldType: "General",
                name: fields.imageColorOverlayGradient,
                displayName: "Image Color Overlay Gradient",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.breadcrumbsEnabled,
                displayName: "Breadcrumbs",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
        ],
    },
};

export default newUCSBanner;
